// src/layouts/LoggedInLayout/Components/Header/Header.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiBell, FiMenu, FiSettings, FiLogOut } from "react-icons/fi";
import { HiChevronDown } from "react-icons/hi";
import { useAuthStore } from '../../../stores/useAuthStore';
import useNotificationStore from '../../../stores/NotificationStore';
import { useGlobalSearchStore } from '../../../stores/GlobalSearchStore';
import NotificationCenter from '../../../Components/NotificationCenter/NotificationCenter';
import GlobalSearch from '../../../Components/GlobalSearch/GlobalSearch';
import userService from '../../../services/UserService';
import logo from "../../../assets/Header/magnetix.png"
import './Header.css';

interface HeaderProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { unreadCount, fetchUnreadCount } = useNotificationStore();
  const { isOpen: isSearchOpen, setIsOpen: setIsSearchOpen, setUserRole, loadTrending } = useGlobalSearchStore();

  // Fresh user data from API (not cached session)
  const [freshUserData, setFreshUserData] = useState<{
    avatar?: string;
    name?: string;
    email?: string;
  } | null>(null);
  const [loadingUserData, setLoadingUserData] = useState(false);

  // Track avatar changes to bust browser cache
  const [avatarTimestamp, setAvatarTimestamp] = useState(Date.now());
  const previousAvatarRef = useRef(user?.avatar);

  const toggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  // Fetch fresh user data from API
  const fetchFreshUserData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoadingUserData(true);
      console.log('[Header] Fetching fresh user data from API...');

      const userData = await userService.getUser(user.id);

      // Build the display name defensively: users without a profile row (e.g.
      // some admins/super-admins) have null first/last names, which would
      // otherwise render as "null null". Fall back to the email.
      const fullName = [userData.first_name, userData.last_name]
        .map((v) => (v == null || v === 'null' ? '' : String(v).trim()))
        .filter(Boolean)
        .join(' ');
      // Email is shown on its own line, so use its local part as a cleaner
      // fallback name (e.g. "admin@x.com" -> "admin") rather than repeating it.
      const fallbackName = userData.email ? userData.email.split('@')[0] : 'User';

      setFreshUserData({
        avatar: userData.dp,
        name: fullName || fallbackName,
        email: userData.email
      });

      console.log('[Header] Fresh user data fetched:', userData.dp);

      // Update timestamp to bust cache
      setAvatarTimestamp(Date.now());
    } catch (error) {
      console.error('[Header] Error fetching user data:', error);
    } finally {
      setLoadingUserData(false);
    }
  }, [user?.id]);

  // Fetch fresh user data on mount
  useEffect(() => {
    fetchFreshUserData();
  }, [fetchFreshUserData]);

  // Listen for profile update events
  useEffect(() => {
    const handleProfileUpdate = () => {
      console.log('[Header] Profile update event received, refreshing user data...');
      fetchFreshUserData();
    };

    window.addEventListener('profile:updated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profile:updated', handleProfileUpdate);
    };
  }, [fetchFreshUserData]);

  // Update timestamp when avatar changes in auth store (for backwards compatibility)
  useEffect(() => {
    if (user?.avatar && user.avatar !== previousAvatarRef.current) {
      console.log('[Header] Avatar changed in auth store, fetching fresh data...');
      previousAvatarRef.current = user.avatar;
      fetchFreshUserData(); // Fetch fresh data from API
    }
  }, [user?.avatar, fetchFreshUserData]);

  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Set user role in search store and load trending items
  useEffect(() => {
    if (user?.role) {
      setUserRole(user.role);
    }
    loadTrending();
  }, [user?.role, setUserRole, loadTrending]);

  // Global keyboard shortcut (Cmd/Ctrl + K) to open search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setIsSearchOpen]);

  const handleAccountSettings = () => {
    navigate('/account-settings');
    setIsProfileMenuOpen(false);
  };

  const handleSignOut = async () => {
    setIsLoggingOut(true);
    const startTime = Date.now();
    const MINIMUM_LOADING_TIME = 1500; // 1.5 seconds minimum

    try {
      // Clear authentication state and tokens
      await logout();

      // Calculate remaining time to meet minimum loading duration
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MINIMUM_LOADING_TIME - elapsedTime);

      // Wait for remaining time if needed
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      // Navigate to login page with replace to prevent back button access
      navigate('/login', { replace: true });
      setIsProfileMenuOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);

      // Calculate remaining time even on error
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MINIMUM_LOADING_TIME - elapsedTime);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      // Even if logout fails, navigate to login
      navigate('/login', { replace: true });
      setIsProfileMenuOpen(false);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  return (
    <>
      <style>{`
        .logged-in-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 2rem;
          border-radius: 2rem;
          background-color: var(--color-white, #ffffff);
          position: relative;
        }
        [data-theme="dark"] .logged-in-header {
          background-color: var(--color-primary-dark, #262B45);
        }

        .logged-in-header-left { 
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .logged-in-hamburger-btn {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          color: #1f2937;
          padding: 8px;
        }
        [data-theme="dark"] .logged-in-hamburger-btn {
          color: var(--color-white, #ffffff);
        }
        .logged-in-logo-img {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          object-fit: contain;
          padding: 4px;
          transition: transform 0.2s ease;
        }
        .logged-in-logo { 
          display: flex; 
          align-items: center; 
          gap: 8px; 
        }
        .logged-in-logo-icon { 
          width: 36px; 
          height: 36px; 
          border-radius: 50%; 
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
        }
        .logged-in-logo-text {
          font-size: 1.5rem;
          font-weight: 600;
          color: #f97316;
        }
        [data-theme="dark"] .logged-in-logo-text {
          color: var(--color-sunflare, #FEB901);
        }

        .logged-in-header-center { 
          flex: 1; 
          max-width: 400px;
          margin: 0 2rem;
        }
        .logged-in-search-bar {
          display: flex;
          align-items: center;
          background-color: #FFF8F0;
          border: 0.6px solid #F4D06F;
          border-radius: 24px;
          padding: 8px 16px;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .logged-in-search-bar:focus-within {
          background-color: #FFEFD9;
          border-color: #F1C456;
        }
        [data-theme="dark"] .logged-in-search-bar {
          background-color: #353B55;
          border-color: #4A5568;
        }
        [data-theme="dark"] .logged-in-search-bar:focus-within {
          background-color: #3C4261;
          border-color: #FEB901;
        }
        .logged-in-search-icon {
          color: var(--color-primary, #f97316);
          flex-shrink: 0;
        }
        [data-theme="dark"] .logged-in-search-icon {
          color: var(--color-sunflare, #FEB901);
        }
        .logged-in-search-input {
          border: none;
          outline: none;
          width: 100%;
          background-color: transparent;
          font-size: 14px;
          color: #374151;
        }
        [data-theme="dark"] .logged-in-search-input {
          color: var(--color-white, #ffffff);
        }
        .logged-in-search-input::placeholder {
          color: var(--color-primary, #f97316);
        }
        [data-theme="dark"] .logged-in-search-input::placeholder {
          color: #9ca3af;
        }

        .logged-in-header-right { 
          display: flex; 
          align-items: center; 
          gap: 16px; 
        }
        
        .logged-in-toggle-switch {
          width: 60px;
          height: 30px;
          background-color: #f97316;
          border-radius: 3rem;
          position: relative;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .logged-in-toggle-slider {
          width: 26px;
          height: 26px;
          background-color: white;
          border-radius: 50%;
          position: absolute;
          top: 2px;
          left: 2px;
          transition: left 0.3s ease;
          display: flex;
          align-items:center;
          justify-content:center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .toggle-icon {
          font-size: 14px;
          color: #f97316;
        }

        .logged-in-notification-btn {
          position: relative;
          background-color: #1f2937;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background-color 0.2s;
          color: white;
          flex-shrink: 0;
        }
        .logged-in-notification-btn:hover {
          background-color: #374151;
        }
        .notification-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          background: #ff4444;
          color: white;
          border-radius: 10px;
          padding: 2px 6px;
          font-size: 11px;
          font-weight: 600;
          min-width: 18px;
          text-align: center;
        }
        [data-theme="dark"] .logged-in-notification-btn {
          background-color: #353B55;
          color: var(--color-sunflare, #FEB901);
        }
        [data-theme="dark"] .logged-in-notification-btn:hover {
          background-color: #3C4261;
        }

        .logged-in-user-profile {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          flex-shrink: 0;
          position: relative;
          padding: 4px 8px;
          border-radius: 12px;
          transition: background-color 0.2s;
        }
        .logged-in-user-profile:hover {
          background-color: #f3f4f6;
        }
        [data-theme="dark"] .logged-in-user-profile:hover {
          background-color: #353B55;
        }
        .logged-in-user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
        }
        .logged-in-user-name {
          font-size: 14px;
          font-weight: 500;
          color: #1f2937;
        }
        [data-theme="dark"] .logged-in-user-name {
          color: var(--color-white, #ffffff);
        }
        .logged-in-dropdown-icon {
          color: #6b7280;
          transition: transform 0.2s;
        }
        .logged-in-dropdown-icon.open {
          transform: rotate(180deg);
        }
        [data-theme="dark"] .logged-in-dropdown-icon {
          color: #9ca3af;
        }

        /* Profile Dropdown Menu */
        .profile-dropdown-menu {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          min-width: 280px;
          z-index: 1000;
          opacity: 0;
          transform: translateY(-10px);
          pointer-events: none;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        [data-theme="dark"] .profile-dropdown-menu {
          background: #353B55;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
        }
        .profile-dropdown-menu.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: all;
        }

        .profile-dropdown-header {
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        [data-theme="dark"] .profile-dropdown-header {
          border-bottom-color: #4A5568;
        }

        .profile-dropdown-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          object-fit: cover;
        }

        .profile-dropdown-info {
          flex: 1;
        }

        .profile-dropdown-name {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
        }
        [data-theme="dark"] .profile-dropdown-name {
          color: var(--color-white, #ffffff);
        }

        .profile-dropdown-email {
          font-size: 14px;
          color: #6b7280;
          margin: 0;
        }
        [data-theme="dark"] .profile-dropdown-email {
          color: #9ca3af;
        }

        .profile-dropdown-body {
          padding: 8px;
        }

        .profile-dropdown-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 12px;
          cursor: pointer;
          transition: background-color 0.2s;
          color: #1f2937;
          text-decoration: none;
          border: none;
          background: none;
          width: 100%;
          text-align: left;
          font-size: 15px;
        }
        [data-theme="dark"] .profile-dropdown-item {
          color: var(--color-white, #ffffff);
        }
        .profile-dropdown-item:hover {
          background-color: #f3f4f6;
        }
        [data-theme="dark"] .profile-dropdown-item:hover {
          background-color: #3C4261;
        }

        .profile-dropdown-item-icon {
          width: 20px;
          height: 20px;
          color: #6b7280;
        }
        [data-theme="dark"] .profile-dropdown-item-icon {
          color: #9ca3af;
        }

        .profile-dropdown-item.sign-out {
          color: #ef4444;
        }
        .profile-dropdown-item.sign-out .profile-dropdown-item-icon {
          color: #ef4444;
        }
        .profile-dropdown-item.sign-out:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .logout-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(239, 68, 68, 0.3);
          border-top-color: #ef4444;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Responsive styles for screens below 900px */
        @media (max-width: 990px) {
          .logged-in-header {
            padding: 0.5rem 1rem;
          }

          .logged-in-hamburger-btn {
            display: block;
          }

          .logged-in-header-center {
            display: none;
          }

          .logged-in-header-right {
            gap: 12px;
          }

          .logged-in-user-name {
            display: none;
          }

          .profile-dropdown-menu {
            right: 0;
          }
        }

        @media (max-width: 600px) {
          .logged-in-header {
            padding: 0.5rem 0.75rem;
          }

          .logged-in-header-right {
            gap: 8px;
          }

          .profile-dropdown-menu {
            min-width: 260px;
          }
        }

        @media (max-width: 500px) {
          .logged-in-header {
            padding: 0.4rem 0.5rem;
          }

          .logged-in-hamburger-btn {
            padding: 4px;
          }

          .logged-in-logo-icon {
            width: 28px;
            height: 28px;
          }

          .logged-in-logo-text {
            font-size: 1.125rem;
          }

          .logged-in-toggle-switch {
            width: 48px;
            height: 24px;
          }

          .logged-in-toggle-slider {
            width: 20px;
            height: 20px;
            left: 2px;
          }

          .toggle-icon {
            font-size: 11px;
          }

          .logged-in-notification-btn {
            width: 32px;
            height: 32px;
          }

          .logged-in-user-avatar {
            width: 32px;
            height: 32px;
          }

          .logged-in-dropdown-icon {
            width: 14px;
            height: 14px;
          }

          .logged-in-header-right {
            gap: 6px;
          }

          .logged-in-header-left {
            gap: 8px;
          }

          .profile-dropdown-menu {
            min-width: 240px;
            right: -8px;
          }
        }
      `}</style>

      <div className="logged-in-header">
        {/* Left - Hamburger Menu + Logo */}
        <div className="logged-in-header-left">
          <button 
            className="logged-in-hamburger-btn"
            onClick={toggleMenu}
            aria-label="Open menu"
          >
            <FiMenu size={24} />
          </button>
          
          <div className="logged-in-logo">
            <img src={logo} alt="Magnetix Logo" className="logged-in-logo-img" />
            <span className="logged-in-logo-text">Magnetix</span>
          </div>
        </div>

        {/* Center - Search Bar (hidden on mobile) */}
        <div className="logged-in-header-center">
          <div className="logged-in-search-bar" onClick={() => setIsSearchOpen(true)}>
            <FiSearch size={20} className="logged-in-search-icon" />
            <input
              type="text"
              placeholder="Search (Ctrl/Cmd + K)"
              className="logged-in-search-input"
              readOnly
            />
          </div>
        </div>

        {/* Right - Theme, Notifications, Profile */}
        <div className="logged-in-header-right">
          {/* Theme Toggle — temporarily hidden until dark mode is fully implemented
          <div className="logged-in-theme-toggle" onClick={toggleTheme}>
            <div className="logged-in-toggle-switch">
              <div className="logged-in-toggle-slider">
                {isDarkMode ? (
                  <RiMoonFill className="toggle-icon" />
                ) : (
                  <RiSunFill className="toggle-icon" />
                )}
              </div>
            </div>
          </div>
          */}

          {/* Notifications */}
          <button 
            className="logged-in-notification-btn"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <FiBell size={20} />
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          <NotificationCenter 
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
          />

          {/* Organization badge — visible to every authenticated user. */}
          {(user as any)?.primaryOrganization?.name && (
            <div
              className="logged-in-org-badge"
              title={
                Array.isArray((user as any).organizations) && (user as any).organizations.length > 1
                  ? `${(user as any).organizations.length} organizations`
                  : (user as any).primaryOrganization.name
              }
            >
              <span className="logged-in-org-badge-label">Org</span>
              <span className="logged-in-org-badge-name">
                {(user as any).primaryOrganization.name}
              </span>
            </div>
          )}

          {/* User Profile with Dropdown */}
          <div ref={profileMenuRef}>
            <div className="logged-in-user-profile" onClick={toggleProfileMenu}>
              <img
                src={
                  freshUserData?.avatar
                    ? `${process.env.REACT_APP_SOCKET_URL}/uploads/users/profile_picture/${freshUserData.avatar}?t=${avatarTimestamp}`
                    : user?.avatar
                      ? `${process.env.REACT_APP_SOCKET_URL}/uploads/users/profile_picture/${user.avatar}?t=${avatarTimestamp}`
                      : "https://via.placeholder.com/100?text=" + (freshUserData?.name?.charAt(0) || user?.name?.charAt(0) || 'U')
                }
                alt={freshUserData?.name || user?.name || 'User'}
                className="logged-in-user-avatar"
              />
              <span className="logged-in-user-name">{freshUserData?.name || user?.name || 'User'}</span>
              <HiChevronDown
                size={16}
                className={`logged-in-dropdown-icon ${isProfileMenuOpen ? 'open' : ''}`}
              />
            </div>

            {/* Dropdown Menu */}
            <div className={`profile-dropdown-menu ${isProfileMenuOpen ? 'open' : ''}`}>
              <div className="profile-dropdown-header">
                <img
                  src={
                    freshUserData?.avatar
                      ? `${process.env.REACT_APP_SOCKET_URL}/uploads/users/profile_picture/${freshUserData.avatar}?t=${avatarTimestamp}`
                      : user?.avatar
                        ? `${process.env.REACT_APP_SOCKET_URL}/uploads/users/profile_picture/${user.avatar}?t=${avatarTimestamp}`
                        : "https://via.placeholder.com/100?text=" + (freshUserData?.name?.charAt(0) || user?.name?.charAt(0) || 'U')
                  }
                  alt={freshUserData?.name || user?.name || 'User'}
                  className="profile-dropdown-avatar"
                />
                <div className="profile-dropdown-info">
                  <h3 className="profile-dropdown-name">{freshUserData?.name || user?.name || 'User'}</h3>
                  <p className="profile-dropdown-email">{freshUserData?.email || user?.email || ''}</p>
                  {(user as any)?.primaryOrganization?.name && (
                    <p className="profile-dropdown-org">
                      🏢 {(user as any).primaryOrganization.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="profile-dropdown-body">
                <button className="profile-dropdown-item" onClick={handleAccountSettings}>
                  <FiSettings className="profile-dropdown-item-icon" size={20} />
                  Account Settings
                </button>

                <button
                  className="profile-dropdown-item sign-out"
                  onClick={handleSignOut}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? (
                    <div className="logout-spinner" />
                  ) : (
                    <FiLogOut className="profile-dropdown-item-icon" size={20} />
                  )}
                  {isLoggingOut ? 'Signing Out...' : 'Sign Out'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Global Search Modal */}
      <GlobalSearch isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Header;